import "./ra-dashboard-assessments.scss";

import { uniqBy } from "lodash";
import { observer } from "mobx-react";
import React from "react";
import { generatePath, Link } from "react-router-dom";

import { Icon, MiniTag, TruncateText, Typography } from "@/components/Elements";
import Dropdown, {
  DropdownOption,
} from "@/components/Elements/Dropdown/Dropdown";
import { formatPercentage } from "@/components/helpers/number-formatters";
import { useMainStore } from "@/contexts/Store";
import {
  RISK_ASSESSMENT_STATUS_DISPLAY,
  ROUTES,
} from "@/features/risk-assessment";
import ContentCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ContentCell";
import Table from "@/features/risk-assessment/components/Table/GenericTable/Table";
import TableHeaderCell from "@/features/risk-assessment/components/Table/GenericTable/TableHeaderCell";
import TableRow from "@/features/risk-assessment/components/Table/GenericTable/TableRow";
import { getDefinitionsFromScores } from "@/features/risk-assessment/utils/scoring-helpers";

import { AssessmentIndex } from "../../../types/assessment";
import { ScoreCell } from "../../Assessment/AssessmentScoring/ScoreCell";

type Props = {
  assessments: AssessmentIndex[];
  selectedAssessmentTemplateId: number;
  onSelectedAssessmentIdChange: (id: number) => void;
};

function RADashboardAssessments(props: Props) {
  const mainStore = useMainStore();
  const { list: riskMethodologies } = mainStore.riskMethodologies;
  const { workspaceID } = mainStore.context;

  const templateDropdownOptions: DropdownOption[] = [
    { id: 0, name: "View All" },
  ].concat(
    uniqBy(
      props.assessments.map((a) => ({
        id: a.assessment_template_id,
        name: a.name,
      })),
      (option) => option.name,
    ).sort((a, b) => a.name.localeCompare(b.name)),
  );
  const filteredAssessments = props.selectedAssessmentTemplateId
    ? props.assessments.filter(
        (a) => a.assessment_template_id == props.selectedAssessmentTemplateId,
      )
    : props.assessments;

  return (
    <div className="ra-dashboard-assessments">
      <div className="ra-dashboard-assessments__title-wrapper">
        <Typography
          label="Partner Assessments"
          color="generalMidnightDark"
          weight="semiBold"
        />
        <Dropdown
          className="ra-dashboard-assessments__template-dropdown"
          options={templateDropdownOptions}
          selected={String(props.selectedAssessmentTemplateId)}
          onChange={(selected) =>
            props.onSelectedAssessmentIdChange(Number(selected.id))
          }
        />
      </div>
      <Table
        className="ra-dashboard-assessments__table"
        fullWidth
        header={
          <>
            <TableHeaderCell title="Partner" firstDataHeader />
            <TableHeaderCell title="Assessment" />
            <TableHeaderCell title="Status" />
            <TableHeaderCell
              title="Percent Answered"
              leftIcon={
                <Icon color="extrasBlueGrayDarker" name="priorityMedium" />
              }
            />
            <TableHeaderCell title="Overall" />
            <TableHeaderCell title="Control" />
            <TableHeaderCell title="Inherent" />
            <TableHeaderCell title="Residual" lastDataHeader />
          </>
        }
      >
        {filteredAssessments.map((assessment, i) => {
          const {
            overallRiskRating,
            controlRiskRating,
            inherentRiskRating,
            residualRiskRating,
          } = getDefinitionsFromScores(
            // @ts-expect-error TS(2345) FIXME: Argument of type 'AssessmentScoring | null' is not... Remove this comment to see the full error message
            assessment.scoring,
            // @ts-expect-error TS(18047)
            riskMethodologies.find(
              (rm) => rm.id == assessment.risk_methodology_id,
            ),
          );
          return (
            <TableRow key={assessment.id} rowIndex={i}>
              <ContentCell
                isFirstDataCell
                content={
                  <Link
                    to={generatePath(ROUTES.PARTNERS_ASSESSMENTS_PATH, {
                      workspace_id: Number(workspaceID),
                      record_version_id: assessment.partner_record_version_id,
                    })}
                  >
                    <TruncateText
                      text={assessment.partner_name}
                      toLength={12}
                    />
                  </Link>
                }
              />
              <ContentCell
                content={
                  <Link
                    to={generatePath(ROUTES.ASSESSMENTS_DETAIL_PATH, {
                      assessmentId: assessment.id,
                      workspace_id: Number(workspaceID),
                    })}
                  >
                    <TruncateText text={assessment.name} toLength={12} />
                  </Link>
                }
              />
              <ContentCell
                content={
                  <MiniTag
                    label={
                      RISK_ASSESSMENT_STATUS_DISPLAY[assessment.status].text
                    }
                    theme={
                      RISK_ASSESSMENT_STATUS_DISPLAY[assessment.status].theme
                    }
                  />
                }
              />
              <ContentCell
                content={
                  <Typography
                    weight="semiBold"
                    label={formatPercentage(assessment.completion_percentage)}
                    color="extrasBlueGrayDarker"
                  />
                }
              />
              <ScoreCell
                rating={overallRiskRating}
                score={assessment.scoring?.overall_risk_score}
              />
              <ScoreCell
                rating={controlRiskRating}
                score={assessment.scoring?.control_risk_score}
              />
              <ScoreCell
                rating={inherentRiskRating}
                score={assessment.scoring?.inherent_risk_score}
              />
              <ScoreCell isLastDataCell rating={residualRiskRating} />
            </TableRow>
          );
        })}
      </Table>
    </div>
  );
}

export default observer(RADashboardAssessments);
